var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AskOkMessage, BroadcastToPeerMessage, convertToNumber3, DeleteModelMessage, EnableModelTransformHandlesAction, InteractionFacet, CollisionFacet, PointCloudFacet, PointCloudType, PrimitiveShape, SendMessagesAction, SetSnapToGridParameterAction, ShowTooltipsBehavior, SnapToGridParm, TooltipInfoFacet, TooltipPlacement, UTILITY_ROOM_UID } from "flavr-common";
import { Color, Vector3, Quaternion } from "three";
import { busService } from "../bus";
import { RosBagMessageType, RosBagPointCloudAvailableDataRequestMessage, RosBagPointCloudFromPeerMessage, RosBagPointCloudPeerFloat32ArrayMessage, RosBagPointCloudPlaybackTimeRequestMessage, RosBagPointCloudPlaybackTimeResponseMessage, RosBagPointCloudPlayerMessage, RosBagPointCloudPlayStateRequestMessage } from "../../messages/rosbag/RosBagMessages";
import { peerFileSystemService, peerService } from "../multiplayer/peer";
import { inputService } from "../inputs/input/InputService";
import { PeerAddedMessage } from "../../messages/session/PeerMessages";
import { loadingSpinnerService } from "../loading/LoadingSpinnerService";
import { modelPool } from "@modules/model";
class RosBagUIService {
    constructor() {
        this.connection = busService.connection("RosBagUIService");
        this.handlePeerAddedMessage = (msg) => __awaiter(this, void 0, void 0, function* () {
            // In case someone joins into the session afterwards
            if (!peerService.peerMgr) {
                console.error("Peer Manager was not found!");
                return;
            }
            const peer = peerService.peerMgr.getPeer(msg.clientId);
            if (!peer) {
                console.error("Peer Connection was not found. Client id is: " + msg.clientId);
                return;
            }
            this.connection.ask(new RosBagPointCloudAvailableDataRequestMessage(), (msgs) => {
                for (const msg of msgs) {
                    peer.sendReliableBusMessageCached(msg);
                }
            });
        });
        this.handleBroadCastPeerMessage = (msg) => __awaiter(this, void 0, void 0, function* () {
            try {
                loadingSpinnerService.toggleLoadingSpinner(true);
                const peerFile = peerFileSystemService.subscribePeerRemoteFile(msg.fileId);
                yield peerFile.onLoadedPromise();
                const blob = yield peerFile.getData();
                if (!(blob instanceof Blob))
                    throw new Error("Only blob supported!");
                const arrayBuffer = yield blob.arrayBuffer();
                if (!arrayBuffer)
                    throw new Error("Blob does not contain an arrayBuffer!");
                const dataView = new Uint8Array(arrayBuffer);
                if (ArrayBuffer.isView(dataView)) {
                    const answer = yield this.connection.askByMessageType(new RosBagPointCloudPeerFloat32ArrayMessage(msg.identifier, msg.frameId, dataView, msg.position, msg.rotation, msg.color, msg.pointSize), AskOkMessage);
                    if (answer.result) {
                        let color = new Color();
                        color = color.setHex(msg.color);
                        const utilityRoom = modelPool.getRoomOrThrow(UTILITY_ROOM_UID);
                        const model = modelPool.createModel(msg.identifier, utilityRoom);
                        model.setTransformComponents(msg.position, msg.rotation, [1, 1, 1]);
                        model.setFacet(new PointCloudFacet({
                            type: PointCloudType.RosBagBased,
                            url: "undefined",
                            timeOutInterval: 150,
                            pointSize: msg.pointSize,
                            topics: [],
                            identifier: msg.identifier,
                            useCustomMaterial: true,
                            shareWithOthers: false,
                            color: [color.r, color.g, color.b],
                            position: msg.position,
                            rotation: msg.rotation,
                            useTransform: false,
                            useOnlyLocalTime: false
                        }));
                        model.flushMessages();
                    }
                }
                // Add controls when adding pointclouds
                void this.addKeyControls();
            }
            catch (error) {
                console.error("error: ", error);
            }
        });
        this.connection.subscribeByType(RosBagPointCloudFromPeerMessage, this.handleBroadCastPeerMessage);
        this.connection.subscribeByType(PeerAddedMessage, this.handlePeerAddedMessage);
    }
    createModel(fileName, topic, identifier, position, rotation, color, pointSize, syncWithOthers, useTransform) {
        const utilityRoom = modelPool.getRoomOrThrow(UTILITY_ROOM_UID);
        const label = identifier + "\ndouble click for deletion";
        const model = modelPool.createModel(identifier, utilityRoom);
        model.setTransformComponents(position, new Quaternion().fromArray(rotation), new Vector3(1, 1, 1));
        model.setFacet(new PointCloudFacet({
            type: PointCloudType.RosBagBased,
            url: fileName,
            timeOutInterval: 150,
            pointSize,
            topics: [topic],
            identifier,
            useCustomMaterial: true,
            shareWithOthers: syncWithOthers,
            color: [color.r, color.g, color.b],
            position: convertToNumber3(position),
            rotation,
            useTransform: !useTransform,
            useOnlyLocalTime: false
        }));
        model.setFacet(new TooltipInfoFacet(label, undefined, undefined, TooltipPlacement.RIGHT_OF_MODEL));
        model.setFacet(new CollisionFacet(PrimitiveShape.Sphere, true));
        model.setFacet(new InteractionFacet({
            behaviors: [new ShowTooltipsBehavior()],
            onClick: [
                new EnableModelTransformHandlesAction(true, 1, true),
                new SetSnapToGridParameterAction(SnapToGridParm.SnapAngle, 0.01)
            ],
            onDoubleClick: [
                new SendMessagesAction([
                    new DeleteModelMessage(model.getUID()),
                    new BroadcastToPeerMessage([new DeleteModelMessage(model.getUID())])
                ])
            ]
        }));
        model.flushMessages();
        // Add controls when adding pointclouds
        void this.addKeyControls();
    }
    getLocalPlaybackTime() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = this.connection.askByMessageType(new RosBagPointCloudPlaybackTimeRequestMessage(), RosBagPointCloudPlaybackTimeResponseMessage);
            if (response) {
                return (yield response).localPlaybackTime;
            }
            else
                return 0;
        });
    }
    getLocalPlaybackState() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.connection.askByMessageType(new RosBagPointCloudPlayStateRequestMessage(), AskOkMessage);
            if (response) {
                return response.result;
            }
            else
                return undefined;
        });
    }
    addKeyControls() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!inputService.findAction("RosBagMediaPlayer_TogglePlay"))
                inputService
                    .registerAction("RosBagMediaPlayer_TogglePlay", () => __awaiter(this, void 0, void 0, function* () {
                    const state = yield this.getLocalPlaybackState();
                    const time = yield this.getLocalPlaybackTime();
                    if (state !== undefined) {
                        if (state) {
                            const msg = new RosBagPointCloudPlayerMessage(RosBagMessageType.pause, time);
                            this.connection.sendMultiple([msg, new BroadcastToPeerMessage([msg])]);
                        }
                        else {
                            const msg = new RosBagPointCloudPlayerMessage(RosBagMessageType.play, time);
                            this.connection.sendMultiple([msg, new BroadcastToPeerMessage([msg])]);
                        }
                    }
                }))
                    .addKey("ControlLeft");
            if (!inputService.findAction("RosBagMediaPlayer_TimeStepForward"))
                inputService
                    .registerAction("RosBagMediaPlayer_TimeStepForward", () => {
                    const msg = new RosBagPointCloudPlayerMessage(RosBagMessageType.nextTimeStep, undefined);
                    this.connection.sendMultiple([msg, new BroadcastToPeerMessage([msg])]);
                })
                    .addKey("KeyM");
            if (!inputService.findAction("RosBagMediaPlayer_TimeStepBackward"))
                inputService
                    .registerAction("RosBagMediaPlayer_TimeStepBackward", () => {
                    const msg = new RosBagPointCloudPlayerMessage(RosBagMessageType.previousTimeStep, undefined);
                    this.connection.sendMultiple([msg, new BroadcastToPeerMessage([msg])]);
                })
                    .addKey("KeyN");
            if (!inputService.findAction("RosBagMediaPlayer_SyncAllClients"))
                inputService
                    .registerAction("RosBagMediaPlayer_SyncAllClients", () => {
                    const msg = new RosBagPointCloudPlayerMessage(RosBagMessageType.sync, undefined);
                    this.connection.sendMultiple([msg, new BroadcastToPeerMessage([msg])]);
                })
                    .addKey("KeyB");
        });
    }
}
export const rosBagUIService = new RosBagUIService();
